// frontend/src/components/DocumentViewer.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Typography
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css'; // Import syntax highlighting theme
import { useThemeSettings } from '../contexts/ThemeContext';
import { jsPDF } from 'jspdf';
import * as indexedDB from '../utils/indexedDB';
import * as tokenEncryption from '../utils/tokenEncryption';

/**
 * Get auth token from secure storage with fallback
 */
const getTokenFromStorage = async (key) => {
  try {
    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem(key);
      if (encryptedToken) {
        const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
        if (decryptedToken) {
          return decryptedToken;
        }
      }
    }

    // Fallback to localStorage if IndexedDB fails
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }

    return null;
  } catch (error) {
    console.error('Error retrieving token:', error);
    // Last resort: try localStorage directly
    return localStorage.getItem(key);
  }
};

const DocumentViewer = ({ open, onClose, docId, docTitle }) => {
  const { t } = useThemeSettings();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [content, setContent] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (open && docId) {
      fetchDocument();
    }
  }, [open, docId]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getTokenFromStorage('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.caro.care'}/api/documentation/${docId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch documentation');
      }

      const data = await response.json();
      setContent(data.doc.content);
    } catch (err) {
      console.error('Error fetching document:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!contentRef.current) return;

    try {
      setDownloadingPdf(true);
      setError(null);

      // Initialize PDF with brand colors
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const headerHeight = 25; // Space for header
      const footerHeight = 15; // Space for footer
      const contentWidth = pageWidth - (2 * margin);
      const contentAreaTop = margin + headerHeight;
      const contentAreaBottom = pageHeight - footerHeight;
      let yPosition = contentAreaTop;

      // Brand colors
      const plumMain = [154, 63, 105];
      const plumDark = [112, 44, 77];
      const grey800 = [28, 37, 46];
      const grey500 = [145, 158, 171];
      const grey300 = [223, 227, 232];

      // Helper function to add header to current page
      const addHeader = async () => {
        // Draw top border
        pdf.setDrawColor(...plumMain);
        pdf.setLineWidth(0.5);
        pdf.line(margin, margin + 8, pageWidth - margin, margin + 8);

        // Try to add logo image (PNG works best with jsPDF)
        try {
          const logoImg = new Image();
          logoImg.src = '/logo/logo-full.png';

          // Wait for image to load if not cached
          if (!logoImg.complete) {
            await new Promise((resolve, reject) => {
              logoImg.onload = resolve;
              logoImg.onerror = () => {
                // Fallback to text if image fails
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(...plumMain);
                pdf.text('CARO Technology', margin, margin + 5);
                resolve();
              };
              setTimeout(reject, 1000); // Timeout after 1 second
            });
          }

          if (logoImg.complete && logoImg.naturalHeight !== 0) {
            // Use full logo with proper aspect ratio
            const logoHeight = 6;
            const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
            pdf.addImage(logoImg, 'PNG', margin, margin, logoWidth, logoHeight);
          } else {
            // Fallback to text
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...plumMain);
            pdf.text('CARO Technology', margin, margin + 5);
          }
        } catch (err) {
          // Fallback to text logo
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...plumMain);
          pdf.text('CARO Technology', margin, margin + 5);
        }

        // Add "CONFIDENTIAL" marking
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 72, 66); // Error red for confidential
        pdf.text('CONFIDENTIAL', pageWidth - margin - 35, margin + 5);
      };

      // Helper function to add footer to current page
      const addFooter = (pageNum, totalPages) => {
        const footerY = pageHeight - footerHeight + 5;

        // Draw bottom border
        pdf.setDrawColor(...grey300);
        pdf.setLineWidth(0.3);
        pdf.line(margin, footerY, pageWidth - margin, footerY);

        // Add copyright
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...grey500);
        pdf.text(
          `© ${new Date().getFullYear()} CARO Technology - Confidential`,
          margin,
          footerY + 5
        );

        // Add page number
        pdf.text(
          `Page ${pageNum} of ${totalPages}`,
          pageWidth - margin - 25,
          footerY + 5
        );
      };

      // Helper function to add new page if needed
      const checkPageBreak = async (requiredSpace = 10) => {
        if (yPosition + requiredSpace > contentAreaBottom) {
          pdf.addPage();
          await addHeader(); // Add header to new page
          yPosition = contentAreaTop;
          return true;
        }
        return false;
      };

      // Add header to first page
      await addHeader();

      // Add document title section
      pdf.setTextColor(...plumMain);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      const titleLines = pdf.splitTextToSize(docTitle, contentWidth);
      pdf.text(titleLines, margin, yPosition);
      yPosition += (titleLines.length * 8) + 5;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...grey500);
      pdf.text('CARO Technology - Documentation', margin, yPosition);
      yPosition += 5;

      pdf.setFontSize(8);
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPosition);
      yPosition += 3;

      pdf.setFillColor(...plumMain);
      pdf.rect(margin, yPosition, contentWidth, 0.5, 'F');
      yPosition += 10;

      // Get text content from markdown
      const getTextContent = (element) => {
        if (!element) return '';

        // Remove code highlights and get clean text
        const clone = element.cloneNode(true);

        // Remove script tags
        clone.querySelectorAll('script, style').forEach(el => el.remove());

        let text = clone.textContent || clone.innerText || '';

        // Replace checkmarks and special list markers
        text = text.replace(/✓|✔/g, '√');
        text = text.replace(/✗|✘/g, 'x');
        text = text.replace(/'/g, "'"); // Smart quotes to regular quotes

        // Normalize whitespace but preserve single spaces
        return text.replace(/\s+/g, ' ').trim();
      };

      // Check if text looks like ASCII art or diagram
      const isAsciiArt = (text) => {
        // Count special characters commonly used in ASCII art
        // eslint-disable-next-line no-useless-escape
        const specialChars = (text.match(/[%#\-_|\/\\+=]/g) || []).length;
        const totalChars = text.length;

        // If more than 30% of characters are ASCII art symbols, treat as art
        return totalChars > 20 && (specialChars / totalChars) > 0.3;
      };

      // Process content elements
      const processElement = async (element) => {
        const tagName = element.tagName.toLowerCase();
        const text = getTextContent(element);

        if (!text.trim()) return;

        await checkPageBreak();

        switch (tagName) {
          case 'h1':
            await checkPageBreak(15);
            pdf.setFontSize(20);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...plumMain);
            const h1Lines = pdf.splitTextToSize(text, contentWidth);
            pdf.text(h1Lines, margin, yPosition);
            yPosition += (h1Lines.length * 7) + 3;
            pdf.setFillColor(231, 214, 224); // Plum lighter
            pdf.rect(margin, yPosition, contentWidth, 0.5, 'F');
            yPosition += 6;
            break;

          case 'h2':
            // H2 headings always start on a new page (unless we're at the top of a page already)
            if (yPosition > contentAreaTop + 10) {
              pdf.addPage();
              await addHeader();
              yPosition = contentAreaTop;
            }

            await checkPageBreak(12);
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...plumDark);
            const h2Lines = pdf.splitTextToSize(text, contentWidth);
            pdf.text(h2Lines, margin, yPosition);
            yPosition += (h2Lines.length * 6) + 5;
            break;

          case 'h3':
            await checkPageBreak(10);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...plumDark);
            const h3Lines = pdf.splitTextToSize(text, contentWidth);
            pdf.text(h3Lines, margin, yPosition);
            yPosition += (h3Lines.length * 5.5) + 4;
            break;

          case 'h4':
          case 'h5':
          case 'h6':
            await checkPageBreak(8);
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...plumMain);
            const hLines = pdf.splitTextToSize(text, contentWidth);
            pdf.text(hLines, margin, yPosition);
            yPosition += (hLines.length * 5) + 3;
            break;

          case 'p':
            await checkPageBreak(8);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(...grey800);
            const pLines = pdf.splitTextToSize(text, contentWidth);

            for (const line of pLines) {
              await checkPageBreak(5);
              pdf.text(line, margin, yPosition);
              yPosition += 5;
            }
            yPosition += 2;
            break;

          case 'li':
            await checkPageBreak(6);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(...grey800);
            pdf.text('•', margin + 2, yPosition);
            const liLines = pdf.splitTextToSize(text, contentWidth - 6);
            pdf.text(liLines, margin + 6, yPosition);
            yPosition += (liLines.length * 5) + 1;
            break;

          case 'pre':
          case 'code':
            // Skip if parent is already a pre/code (avoid duplicates)
            if (element.parentElement &&
                (element.parentElement.tagName.toLowerCase() === 'pre' ||
                 element.parentElement.tagName.toLowerCase() === 'code')) {
              return;
            }

            // Get raw text without whitespace normalization for code
            const rawText = element.textContent || element.innerText || '';

            // Check if it's ASCII art
            if (isAsciiArt(rawText)) {
              // Skip ASCII art diagrams as they don't render well in PDF
              await checkPageBreak(6);
              pdf.setFontSize(9);
              pdf.setFont('helvetica', 'italic');
              pdf.setTextColor(...grey500);
              pdf.text('[Diagram omitted - see web version]', margin, yPosition);
              yPosition += 8;
              return;
            }

            // Regular code block
            await checkPageBreak(10);

            // Calculate height needed
            const codeTextLines = rawText.split('\n').slice(0, 15); // Limit to 15 lines
            const estimatedHeight = Math.min((codeTextLines.length * 4.5) + 6, 75);
            await checkPageBreak(estimatedHeight);

            // Draw background
            pdf.setFillColor(244, 246, 248); // Light grey background
            pdf.rect(margin, yPosition - 3, contentWidth, estimatedHeight, 'F');

            // Draw border
            pdf.setDrawColor(223, 227, 232); // Grey border
            pdf.setLineWidth(0.3);
            pdf.rect(margin, yPosition - 3, contentWidth, estimatedHeight);

            // Add code text
            pdf.setFontSize(7);
            pdf.setFont('courier', 'normal');
            pdf.setTextColor(28, 37, 46); // Grey-800 for code text

            let codeY = yPosition;
            codeTextLines.forEach((line, idx) => {
              if (codeY + 5 < yPosition + estimatedHeight - 3) {
                // Truncate very long lines
                const truncatedLine = line.length > 100 ? line.substring(0, 97) + '...' : line;
                pdf.text(truncatedLine, margin + 3, codeY);
                codeY += 4.5;
              }
            });

            if (codeTextLines.length > 15) {
              pdf.setFontSize(7);
              pdf.setFont('helvetica', 'italic');
              pdf.text('[... truncated ...]', margin + 3, codeY);
            }

            yPosition += estimatedHeight + 3;
            break;

          case 'blockquote':
            await checkPageBreak(8);
            pdf.setDrawColor(218, 160, 61); // Gold
            pdf.setLineWidth(1);
            pdf.line(margin, yPosition - 2, margin, yPosition + 8);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(99, 115, 129);
            const quoteLines = pdf.splitTextToSize(text, contentWidth - 6);
            pdf.text(quoteLines, margin + 4, yPosition);
            yPosition += (quoteLines.length * 5) + 4;
            break;

          default:
            // Skip or handle other elements
            break;
        }
      };

      // Helper function to process tables
      const processTable = async (table) => {
        await checkPageBreak(15);

        // Extract table data
        const rows = Array.from(table.querySelectorAll('tr'));
        if (rows.length === 0) return;

        // Get header and body rows
        const headerRow = rows[0];
        const bodyRows = rows.slice(1);

        const headers = Array.from(headerRow.querySelectorAll('th, td')).map(cell =>
          cell.textContent.trim().replace(/\s+/g, ' ')
        );

        const data = bodyRows.map(row =>
          Array.from(row.querySelectorAll('td')).map(cell =>
            cell.textContent.trim().replace(/\s+/g, ' ')
          )
        );

        if (headers.length === 0) return;

        // Calculate column widths (distribute evenly)
        const numCols = headers.length;
        const colWidth = contentWidth / numCols;
        const rowHeight = 8;

        // Draw table header
        pdf.setFillColor(231, 214, 224); // Plum lighter
        pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');

        pdf.setDrawColor(223, 227, 232); // Grey border
        pdf.setLineWidth(0.3);

        // Header text
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...plumDark);

        headers.forEach((header, i) => {
          const x = margin + (i * colWidth);
          // Draw cell border
          pdf.rect(x, yPosition, colWidth, rowHeight);
          // Draw text (truncate if too long)
          const maxChars = Math.floor(colWidth * 2.5);
          const truncatedHeader = header.length > maxChars ? header.substring(0, maxChars - 3) + '...' : header;
          pdf.text(truncatedHeader, x + 2, yPosition + 5);
        });

        yPosition += rowHeight;

        // Draw table body rows
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(...grey800);

        for (const rowData of data) {
          // Check if we need a page break
          await checkPageBreak(rowHeight + 2);

          // Alternate row background
          if (data.indexOf(rowData) % 2 === 1) {
            pdf.setFillColor(249, 250, 251); // Very light grey
            pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');
          }

          const currentY = yPosition;
          rowData.forEach((cell, i) => {
            const x = margin + (i * colWidth);
            // Draw cell border
            pdf.setDrawColor(223, 227, 232);
            pdf.rect(x, currentY, colWidth, rowHeight);
            // Draw text (truncate if too long)
            const maxChars = Math.floor(colWidth * 3);
            const truncatedCell = cell.length > maxChars ? cell.substring(0, maxChars - 3) + '...' : cell;
            pdf.text(truncatedCell, x + 2, currentY + 5);
          });

          yPosition += rowHeight;
        }

        yPosition += 3; // Add spacing after table
      };

      // Process all content elements including tables
      const allElements = contentRef.current.querySelectorAll('h1, h2, h3, h4, h5, h6, p, li, pre, code, blockquote, table');

      for (const element of allElements) {
        try {
          if (element.tagName.toLowerCase() === 'table') {
            await processTable(element);
          } else {
            await processElement(element);
          }
        } catch (elementError) {
          console.warn('Error processing element:', element.tagName, elementError);
        }
      }

      // Add footer to all pages
      const totalPages = pdf.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        addFooter(i, totalPages);
      }

      // Save the PDF
      const fileName = `${docTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      pdf.save(fileName);

    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{docTitle}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <div className="spinner" />
          </Box>
        )}

        {error && (
          <Alert severity="error" className="slide-up" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && content && (
          <Box
            ref={contentRef}
            className="fade-in"
            sx={{
              '& h1': {
                fontSize: '2rem',
                fontWeight: 'bold',
                mt: 3,
                mb: 2,
                borderBottom: '2px solid',
                borderColor: 'divider',
                pb: 1
              },
              '& h2': {
                fontSize: '1.5rem',
                fontWeight: 'bold',
                mt: 2.5,
                mb: 1.5
              },
              '& h3': {
                fontSize: '1.25rem',
                fontWeight: 'bold',
                mt: 2,
                mb: 1
              },
              '& p': {
                mb: 2,
                lineHeight: 1.7
              },
              '& code': {
                backgroundColor: 'action.hover',
                padding: '2px 6px',
                borderRadius: 1,
                fontSize: '0.875rem',
                fontFamily: 'monospace'
              },
              '& pre': {
                backgroundColor: 'action.hover',
                padding: 2,
                borderRadius: 1,
                overflow: 'auto',
                mb: 2,
                '& code': {
                  backgroundColor: 'transparent',
                  padding: 0
                }
              },
              '& ul, & ol': {
                mb: 2,
                pl: 3
              },
              '& li': {
                mb: 0.5
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2,
                ml: 0,
                fontStyle: 'italic',
                color: 'text.secondary'
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2
              },
              '& th': {
                backgroundColor: 'action.hover',
                fontWeight: 'bold',
                padding: 1,
                border: '1px solid',
                borderColor: 'divider'
              },
              '& td': {
                padding: 1,
                border: '1px solid',
                borderColor: 'divider'
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1,
                mt: 2,
                mb: 2
              }
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
            >
              {content}
            </ReactMarkdown>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={downloadPDF}
          variant="outlined"
          color="primary"
          startIcon={downloadingPdf ? <CircularProgress size={20} /> : <DownloadIcon />}
          disabled={downloadingPdf || loading || !!error}
        >
          {downloadingPdf ? 'Generating PDF...' : (t ? t('DeveloperInfo.download_pdf') : 'Download PDF')}
        </Button>
        <Button onClick={onClose} variant="contained">
          {t ? t('DeveloperInfo.close') : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewer;
